import { useState } from 'react'
import { Box, Button, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  RestartAlt as RestartAltIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useDashboardStore } from '@/stores/dashboardStore'
import type { WidgetKey } from '@/types/dashboard'

/**
 * Управление раскладкой: Изменить / Сохранить / Отмена / Сбросить
 * + меню восстановления скрытых виджетов (в режиме редактирования).
 */
export function EditModeToolbar() {
  const { t } = useTranslation()
  const editMode = useDashboardStore((s) => s.editMode)
  const draftItems = useDashboardStore((s) => s.draftItems)
  const isSavingLayout = useDashboardStore((s) => s.isSavingLayout)
  const enterEdit = useDashboardStore((s) => s.enterEdit)
  const cancelEdit = useDashboardStore((s) => s.cancelEdit)
  const saveLayout = useDashboardStore((s) => s.saveLayout)
  const resetLayout = useDashboardStore((s) => s.resetLayout)
  const toggleHidden = useDashboardStore((s) => s.toggleHidden)

  const [hiddenMenuAnchor, setHiddenMenuAnchor] = useState<HTMLElement | null>(null)
  const hiddenItems = (draftItems ?? []).filter((item) => item.hidden)

  const widgetTitle = (key: WidgetKey) => t(`dashboard.widgets.${key}.title`)

  const handleReset = () => {
    if (!window.confirm(t('dashboard.layout.resetConfirm'))) {
      return
    }
    void resetLayout()
  }

  if (!editMode) {
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" sx={{ height: 40 }} startIcon={<EditIcon />} onClick={enterEdit}>
          {t('dashboard.layout.edit')}
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Button
        size="small"
        sx={{ height: 40 }}
        variant="contained"
        startIcon={<CheckIcon />}
        disabled={isSavingLayout}
        onClick={() => void saveLayout()}
      >
        {t('dashboard.layout.save')}
      </Button>
      <Button
        size="small"
        sx={{ height: 40 }}
        startIcon={<CloseIcon />}
        disabled={isSavingLayout}
        onClick={cancelEdit}
      >
        {t('dashboard.layout.cancel')}
      </Button>
      <Button
        size="small"
        sx={{ height: 40 }}
        color="warning"
        startIcon={<RestartAltIcon />}
        disabled={isSavingLayout}
        onClick={handleReset}
      >
        {t('dashboard.layout.reset')}
      </Button>
      {hiddenItems.length > 0 && (
        <>
          <Button
            size="small"
            sx={{ height: 40 }}
            startIcon={<VisibilityIcon />}
            onClick={(event) => setHiddenMenuAnchor(event.currentTarget)}
          >
            {t('dashboard.layout.hidden')} ({hiddenItems.length})
          </Button>
          <Menu
            anchorEl={hiddenMenuAnchor}
            open={Boolean(hiddenMenuAnchor)}
            onClose={() => setHiddenMenuAnchor(null)}
          >
            {hiddenItems.map((item) => (
              <MenuItem
                key={item.widget_key}
                onClick={() => {
                  toggleHidden(item.widget_key)
                  setHiddenMenuAnchor(null)
                }}
              >
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{widgetTitle(item.widget_key)}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  )
}
